<template>
  <div class="container-fluid py-4">
    <div class="row">
      <div class="col-12">
        <div class="card shadow-sm border-0 mb-4">  
          <div class="card-header bg-white pb-0">
            <h5 class="font-weight-bolder text-dark">📢 Live Marquee Ticker Broadcast</h5>
            <p class="text-sm text-muted">Select target displays and broadcast or update custom footer announcements in real-time.</p>
          </div>
          <div class="card-body">
            
            <!-- 1. Text Input for Marquee Message -->
            <div class="form-group mb-4">
              <label class="form-control-label font-weight-bold text-xs text-uppercase text-secondary">Announcement Message</label>
              <textarea 
                v-model="marqueeText" 
                class="form-control" 
                rows="3" 
                placeholder="Type your custom announcement message here (e.g. Counter 3 closed for lunch)...">
              </textarea>
            </div>

            <!-- 2. Device Selection & Live Status Checklist -->
            <div class="form-group mb-4">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <label class="form-control-label font-weight-bold text-xs text-uppercase text-secondary">Target Displays (Select TVs)</label>
                <div>
                  <button @click="selectAll" class="btn btn-link text-primary btn-sm p-0 mr-3">Select All</button>
                  <button @click="deselectAll" class="btn btn-link text-danger btn-sm p-0">Deselect All</button>
                </div>
              </div>

              <div class="border rounded p-3 bg-light" style="max-height: 320px; overflow-y: auto;">
                <div v-if="devices.length === 0" class="text-center text-muted py-3">
                  Loading connected devices...
                </div>
                
                <div v-for="device in devices" :key="device.device_id" class="d-flex justify-content-between align-items-center p-2 mb-2 bg-white rounded border">
                  <div class="custom-control custom-checkbox">
                    <input 
                      type="checkbox" 
                      class="custom-control-input" 
                      :id="'dev_' + device.device_id" 
                      :value="device.device_hw_id" 
                      v-model="selectedDeviceHwIds">
                    <label class="custom-control-label font-weight-bold text-dark mb-0" :for="'dev_' + device.device_id">
                      {{ device.device_name || 'Unnamed Display' }}
                      <span class="badge badge-sm ml-2" :class="device.status === 'Online' ? 'bg-gradient-success' : 'bg-gradient-secondary'">
                        {{ device.status || 'Offline' }}
                      </span>
                    </label>
                  </div>

                  <!-- Live Active Announcement Badge + History Icon + Reset Button -->
                  <div class="d-flex align-items-center">
                    <span v-if="activeMarquees[device.device_hw_id]" class="text-xs text-primary font-weight-bold mr-3">
                      Current: "{{ activeMarquees[device.device_hw_id] }}"
                    </span>
                    <span v-else class="text-xs text-muted font-italic mr-3">
                      Current: Aerion Powered By Axeliva Cloud
                    </span>

                    <!-- 🕒 History Icon Button -->
                    <button 
                      @click="fetchHistory(device.device_hw_id, device.device_name)" 
                      class="btn btn-outline-info btn-xs mb-0 py-1 px-2 mr-2"
                      title="View Broadcast History">
                      🕒 History
                    </button>

                    <button 
                      v-if="activeMarquees[device.device_hw_id]"
                      @click="resetSingleDevice(device.device_hw_id)" 
                      class="btn btn-outline-danger btn-xs mb-0 py-1 px-2"
                      title="Revert to Default">
                      Reset
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- 3. Action Buttons -->
            <div class="d-flex">
              <button @click="sendBroadcast" class="btn bg-gradient-primary flex-grow-1 py-3 text-white font-weight-bold shadow mr-2">
                🚀 BROADCAST TO SELECTED SCREENS
              </button>
              <button @click="resetSelected" class="btn btn-outline-danger py-3 font-weight-bold shadow-sm">
                🔄 REVERT TO DEFAULT
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { io } from 'socket.io-client';
import Swal from 'sweetalert2';

export default {
  name: 'MarqueeBroadcast',
  data() {
    const envUrl = process.env.VUE_APP_API_BASE_URL || 'http://localhost:3005/api';
    const rootUrl = envUrl.replace(/\/api\/?$/, '');

    return {
      devices: [],
      selectedDeviceHwIds: [],
      marqueeText: '',
      activeMarquees: {}, 
      socket: null,
      apiBaseUrl: rootUrl, 
    };
  },
  mounted() {
    this.fetchDevices();
    this.initSocket();
  },
  beforeUnmount() {
    if (this.socket) {
      this.socket.disconnect();
    }
  },
  methods: {
    getAuthHeader() {
      const token = localStorage.getItem('auth_token') 
                 || localStorage.getItem('token') 
                 || localStorage.getItem('authToken') 
                 || '';
      return { headers: { Authorization: `Bearer ${token}` } };
    },
    async fetchDevices() {
      try {
        const res = await axios.get(`${this.apiBaseUrl}/api/v1/admin/user-device-list`, this.getAuthHeader());
        this.devices = res.data || [];
      } catch (err) {
        console.error("Failed to load devices:", err);
      }
    },
    initSocket() {
      const token = localStorage.getItem('auth_token') 
                 || localStorage.getItem('token') 
                 || localStorage.getItem('authToken') 
                 || '';
      
      console.log("🔗 Attempting socket connection to:", this.apiBaseUrl);

      this.socket = io(this.apiBaseUrl, {
        auth: { token: token },
        transports: ['websocket'] // 🚀 FORCES PURE WEBSOCKET: Eliminates HTTP Polling CORS error completely!
      });

      this.socket.on('connect', async () => {
        console.log("✅ Socket.IO connected successfully! ID:", this.socket.id);
        try {
          const res = await axios.get(`${this.apiBaseUrl}/api/v1/admin/device/marquee/active`, this.getAuthHeader());
          if (res.data && res.data.data) {
            this.activeMarquees = res.data.data;
          }
        } catch (err) {
          console.warn("Could not load active marquees:", err.message);
        }
      });

      this.socket.on('connect_error', (error) => {
        console.error("❌ Socket connection error:", error.message);
      });

      this.socket.on('disconnect', (reason) => {
        console.warn("⚠️ Socket disconnected:", reason);
      });

      this.socket.on('active_marquees_response', (data) => {
        this.activeMarquees = data || {};
      });

      this.socket.on('active_marquees_updated', (data) => {
        this.activeMarquees = data || {};
      });
    },
    selectAll() {
      this.selectedDeviceHwIds = this.devices.map(d => d.device_hw_id).filter(Boolean);
    },
    deselectAll() {
      this.selectedDeviceHwIds = [];
    },

    async sendBroadcast() {
      if (!this.marqueeText.trim()) {
        Swal.fire('Error', 'Please enter a marquee message to broadcast.', 'error');
        return;
      }
      if (this.selectedDeviceHwIds.length === 0) {
        Swal.fire('Error', 'Please select at least one target device.', 'error');
        return;
      }

      const broadcastMsg = this.marqueeText.trim();
      const targetIds = [...this.selectedDeviceHwIds];

      try {
        const selectedDevicesMap = this.devices
          .filter(d => targetIds.includes(d.device_hw_id))
          .map(d => ({ hwId: d.device_hw_id, name: d.device_name || 'Unnamed Display' }));

        const userObj = JSON.parse(localStorage.getItem('user') || '{}');
        const loggedInName = userObj.name || localStorage.getItem('userName') || 'Super Admin';

        const payload = {
          targetDeviceHwIds: targetIds,
          targetDeviceNames: selectedDevicesMap,
          text: broadcastMsg,
          broadcast_by: loggedInName, 
          reset: false
        };

        const res = await axios.post(
          `${this.apiBaseUrl}/api/v1/admin/device/marquee/broadcast`,
          payload,
          this.getAuthHeader()
        );

        if (res.data && res.data.success) {
          // 🚀 INSTANT LOCAL UI UPDATE: Row badge updates immediately without waiting for socket!
          targetIds.forEach(hwId => {
            this.activeMarquees[hwId] = broadcastMsg;
          });

          Swal.fire({
            title: 'Broadcast Sent!',
            text: `Successfully broadcasted to ${targetIds.length} screens.`,    
            icon: 'success',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000
          });

          this.marqueeText = '';
          this.selectedDeviceHwIds = [];
        }
      } catch (err) {
        console.error("Broadcast failed:", err);
        Swal.fire('Error', 'Failed to send broadcast via API.', 'error');
      }
    },
    async fetchHistory(hwId, devName) {
      try {
        const res = await axios.get(`${this.apiBaseUrl}/api/v1/admin/device/marquee/history?hwId=${encodeURIComponent(hwId)}`, this.getAuthHeader());
        const logs = res.data.data || [];

        let htmlContent = `<div style="max-height:300px; overflow-y:auto; text-align:left;">`;
        if (logs.length === 0) {
          htmlContent += `<p class="text-muted text-center">No broadcast history found for ${devName}.</p>`;
        } else {
          logs.forEach(log => {
            htmlContent += `
              <div class="border-bottom py-2 text-left">
                <strong>Message:</strong> "${log.message}"<br>
                <small class="text-info">Broadcast By: <b>${log.broadcast_by || 'Admin'}</b></small><br>
                <small class="text-muted">Start: ${new Date(log.start_time).toLocaleString()}</small><br>
                <small class="text-muted">End: ${log.end_time ? new Date(log.end_time).toLocaleString() : 'Active Now'}</small>
              </div>`;
          });
        }
        htmlContent += `</div>`;

        Swal.fire({
          title: `🕒 History for ${devName}`,
          html: htmlContent,
          width: 600,
          confirmButtonText: 'Close'
        });
      } catch (err) {
        Swal.fire('Error', 'Could not load broadcast history.', 'error');
      }
    },
   
    async resetSelected() {
      if (this.selectedDeviceHwIds.length === 0) {
        Swal.fire('Error', 'Please select at least one device to revert.', 'error');
        return;
      }

      const targetIds = [...this.selectedDeviceHwIds];

      try {
        const payload = {
          targetDeviceHwIds: targetIds,
          text: '',
          reset: true
        };

        const res = await axios.post(
          `${this.apiBaseUrl}/api/v1/admin/device/marquee/broadcast`,
          payload,
          this.getAuthHeader()
        );

        if (res.data && res.data.success) {
          // 🚀 INSTANT LOCAL UI RESET
          targetIds.forEach(hwId => {
            delete this.activeMarquees[hwId];
          });

          Swal.fire({
            title: 'Reverted to Default!',
            text: 'Selected screens reverted to default bank announcement.',
            icon: 'info',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000
          });

          this.selectedDeviceHwIds = [];
        }
      } catch (err) {
        console.error("Reset failed:", err);
      }
    },

    // 🚀 Reset a single device via HTTP POST
    async resetSingleDevice(hwId) {
      try {
        await axios.post(
          `${this.apiBaseUrl}/api/v1/admin/device/marquee/broadcast`,
          { targetDeviceHwIds: [hwId], text: '', reset: true },
          this.getAuthHeader()
        );
        delete this.activeMarquees[hwId];
      } catch (err) {
        console.error("Single reset failed:", err);
      }
    }
  }
};
</script>